import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Users,
  IndianRupee,
  RefreshCw,
  Crown,
  Building2,
  TrendingUp,
  Send,
  History,
  Settings,
  AlertCircle,
} from "lucide-react";

export const CityAdminEarningsManagement = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [cityRates, setCityRates] = useState([]);
  const [showPayoutDialog, setShowPayoutDialog] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [showCityRatesDialog, setShowCityRatesDialog] = useState(false);
  const [selectedCityAdmin, setSelectedCityAdmin] = useState(null);
  const [selectedCityForRate, setSelectedCityForRate] = useState(null);
  const [cityRatePercentage, setCityRatePercentage] = useState(20);
  const [payoutAmount, setPayoutAmount] = useState("");
  const [payoutMethod, setPayoutMethod] = useState("cash");
  const [payoutRef, setPayoutRef] = useState("");
  const [payoutNotes, setPayoutNotes] = useState("");
  const [commissionPercentage, setCommissionPercentage] = useState(20);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (user?.id) {
      fetchData();
      fetchCityRates();
    }
  }, [user?.id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/${user.id}/city-admins-earnings`);
      setData(res.data);
      setCommissionPercentage(res.data.default_commission_percentage || 20);
    } catch (err) {
      toast.error("Failed to load city admin earnings");
    } finally {
      setLoading(false);
    }
  };

  const fetchCityRates = async () => {
    try {
      const res = await axios.get(`${API}/admin/${user.id}/cities-commission-rates`);
      setCityRates(res.data.cities || []);
    } catch (err) {
      console.error("Failed to fetch city rates", err);
    }
  };

  const handleSaveCityRate = async () => {
    if (!selectedCityForRate) return;
    setActionLoading(true);
    try {
      await axios.put(
        `${API}/admin/${user.id}/city/${selectedCityForRate.city_id}/commission-percentage?percentage=${cityRatePercentage}`
      );
      toast.success(`Commission for ${selectedCityForRate.city_name} set to ${cityRatePercentage}%`);
      setShowCityRatesDialog(false);
      setSelectedCityForRate(null);
      fetchCityRates();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update city rate");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSetPrimary = async (cityAdminId) => {
    try {
      await axios.put(`${API}/admin/city-admin/${cityAdminId}/set-primary`);
      toast.success("Primary City Admin updated");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to set primary");
    }
  };

  const handleRecordPayout = async () => {
    if (!payoutAmount || parseFloat(payoutAmount) <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    setActionLoading(true);
    try {
      await axios.post(`${API}/admin/city-admin/${selectedCityAdmin.city_admin_id}/payout`, {
        amount: parseFloat(payoutAmount),
        payment_method: payoutMethod,
        reference_id: payoutRef || undefined,
        notes: payoutNotes || undefined
      });
      toast.success(`Payout of ₹${payoutAmount} recorded`);
      setShowPayoutDialog(false);
      setPayoutAmount("");
      setPayoutRef("");
      setPayoutNotes("");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to record payout");
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdatePercentage = async () => {
    setActionLoading(true);
    try {
      await axios.put(`${API}/admin/${user.id}/city-admin-commission-percentage?percentage=${commissionPercentage}`);
      toast.success(`Commission percentage updated to ${commissionPercentage}%`);
      setShowSettingsDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="city-admin-earnings-management">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="w-6 h-6" /> City Admin Earnings & Payouts
          </h1>
          <p className="text-sm text-muted-foreground">
            Manage commission payments to your City Admins
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowSettingsDialog(true)}>
            <Settings className="w-4 h-4 mr-2" /> Settings
          </Button>
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-5 h-5 text-blue-500" />
            <span className="text-sm text-blue-600">Total City Admins</span>
          </div>
          <p className="text-3xl font-bold text-blue-700">{data?.summary?.total_city_admins || 0}</p>
          <p className="text-xs text-blue-500">{data?.summary?.primary_city_admins || 0} primary</p>
        </div>
        
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <IndianRupee className="w-5 h-5 text-yellow-600" />
            <span className="text-sm text-yellow-600">Default Rate</span>
          </div>
          <p className="text-3xl font-bold text-yellow-700">{data?.default_commission_percentage || 20}%</p>
          <p className="text-xs text-yellow-500">of your commission</p>
        </div>
        
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <span className="text-sm text-red-600">Total Owed</span>
          </div>
          <p className="text-3xl font-bold text-red-700">₹{data?.summary?.total_owed_to_city_admins?.toLocaleString() || 0}</p>
        </div>
        
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-5 h-5 text-green-500" />
            <span className="text-sm text-green-600">Total Paid</span>
          </div>
          <p className="text-3xl font-bold text-green-700">₹{data?.summary?.total_paid_to_city_admins?.toLocaleString() || 0}</p>
        </div>
      </div>

      {/* City-Specific Commission Rates */}
      {cityRates.length > 0 && (
        <div className="bg-card border rounded-lg p-4">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary" />
            City-Specific Commission Rates
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Set different commission percentages for each city. The default rate ({data?.default_commission_percentage || 20}%) applies to cities without custom rates.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {cityRates.map((city) => (
              <div 
                key={city.city_id} 
                className={`p-3 rounded-lg border ${city.is_custom ? 'border-primary bg-primary/5' : 'border-border bg-muted/30'}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-sm">{city.city_name}</span>
                  {city.is_custom && (
                    <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded">Custom</span>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className={`text-2xl font-bold ${city.is_custom ? 'text-primary' : 'text-muted-foreground'}`}>
                    {city.commission_percentage}%
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setSelectedCityForRate(city);
                      setCityRatePercentage(city.commission_percentage);
                      setShowCityRatesDialog(true);
                    }}
                  >
                    Edit
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* City Admins Table */}
      <div className="bg-card border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>City Admin</TableHead>
              <TableHead>City</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-center">Rate</TableHead>
              <TableHead className="text-right">Drivers</TableHead>
              <TableHead className="text-right">Rides</TableHead>
              <TableHead className="text-right">Admin Commission</TableHead>
              <TableHead className="text-right">Their Share</TableHead>
              <TableHead className="text-right">Paid</TableHead>
              <TableHead className="text-right">Dues</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(data?.city_admins || []).length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} className="text-center py-8 text-muted-foreground">
                  No City Admins found
                </TableCell>
              </TableRow>
            ) : (
              data.city_admins.map((ca) => (
                <TableRow key={ca.city_admin_id} className={!ca.is_primary ? "opacity-60" : ""}>
                  <TableCell>
                    <div>
                      <p className="font-medium flex items-center gap-1">
                        {ca.city_admin_name}
                        {ca.is_primary && <Crown className="w-4 h-4 text-yellow-500" />}
                      </p>
                      <p className="text-sm text-muted-foreground">{ca.city_admin_phone}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Building2 className="w-4 h-4 text-muted-foreground" />
                      {ca.city_name || "No city"}
                    </div>
                  </TableCell>
                  <TableCell>
                    {ca.is_primary ? (
                      <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs font-medium">Primary</span>
                    ) : (
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">Secondary</span>
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      ca.commission_percentage !== data?.default_commission_percentage 
                        ? 'bg-primary/10 text-primary' 
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {ca.commission_percentage}%
                    </span>
                  </TableCell>
                  <TableCell className="text-right">{ca.drivers_count}</TableCell>
                  <TableCell className="text-right">{ca.rides_count}</TableCell>
                  <TableCell className="text-right">₹{ca.admin_commission_from_city?.toLocaleString()}</TableCell>
                  <TableCell className="text-right font-medium">
                    ₹{ca.total_earnings?.toLocaleString()}
                    {!ca.is_primary && ca.city_id && (
                      <p className="text-xs text-muted-foreground">₹0 (not primary)</p>
                    )}
                  </TableCell>
                  <TableCell className="text-right text-green-600">₹{ca.total_paid?.toLocaleString()}</TableCell>
                  <TableCell className="text-right">
                    <span className={ca.pending_dues > 0 ? "text-red-600 font-semibold" : "text-green-600"}>
                      ₹{ca.pending_dues?.toLocaleString()}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {ca.is_primary && ca.pending_dues > 0 && (
                        <Button
                          size="sm"
                          onClick={() => {
                            setSelectedCityAdmin(ca);
                            setPayoutAmount(ca.pending_dues.toString());
                            setShowPayoutDialog(true);
                          }}
                        >
                          <Send className="w-4 h-4 mr-1" /> Pay
                        </Button>
                      )}
                      {!ca.is_primary && ca.city_id && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSetPrimary(ca.city_admin_id)}
                        >
                          <Crown className="w-4 h-4 mr-1" /> Set Primary
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Payout Dialog */}
      <Dialog open={showPayoutDialog} onOpenChange={setShowPayoutDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Payout to {selectedCityAdmin?.city_admin_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Amount (₹)</Label>
              <Input
                type="number"
                value={payoutAmount}
                onChange={(e) => setPayoutAmount(e.target.value)}
                placeholder="Enter amount"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Pending: ₹{selectedCityAdmin?.pending_dues?.toLocaleString()}
              </p>
            </div>
            <div>
              <Label>Payment Method</Label>
              <Select value={payoutMethod} onValueChange={setPayoutMethod}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reference ID (optional)</Label>
              <Input
                value={payoutRef}
                onChange={(e) => setPayoutRef(e.target.value)}
                placeholder="Transaction ID / UTR"
              />
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Input
                value={payoutNotes}
                onChange={(e) => setPayoutNotes(e.target.value)}
                placeholder="Payment notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayoutDialog(false)}>Cancel</Button>
            <Button onClick={handleRecordPayout} disabled={actionLoading}>
              {actionLoading ? "Recording..." : "Record Payout"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>City Admin Commission Settings</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Default Commission Percentage</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  value={commissionPercentage}
                  onChange={(e) => setCommissionPercentage(parseFloat(e.target.value) || 0)}
                  min="0"
                  max="100"
                  className="w-24"
                />
                <span className="text-muted-foreground">% of your commission</span>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                This is the default percentage. You can set city-specific rates in the "City-Specific Commission Rates" section.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettingsDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdatePercentage} disabled={actionLoading}>
              {actionLoading ? "Saving..." : "Save Settings"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* City Rate Dialog */}
      <Dialog open={showCityRatesDialog} onOpenChange={setShowCityRatesDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-primary" />
              Set Commission for {selectedCityForRate?.city_name}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="p-4 bg-muted/50 rounded-lg">
              <p className="text-sm text-muted-foreground mb-2">Current Rate</p>
              <p className="text-2xl font-bold">
                {selectedCityForRate?.commission_percentage}%
                {selectedCityForRate?.is_custom && (
                  <span className="text-sm font-normal text-primary ml-2">(Custom)</span>
                )}
              </p>
            </div>
            
            <div>
              <Label>New Commission Percentage</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  type="number"
                  value={cityRatePercentage}
                  onChange={(e) => setCityRatePercentage(parseFloat(e.target.value) || 0)}
                  min="0"
                  max="100"
                  className="w-24"
                />
                <span className="text-muted-foreground">% of your commission</span>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Primary City Admin for {selectedCityForRate?.city_name} will receive this percentage.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCityRatesDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveCityRate} disabled={actionLoading}>
              {actionLoading ? "Saving..." : "Save Rate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CityAdminEarningsManagement;
